import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const i18n = defineMessages({
	promptHeading: {
		id: 'byline-connections.preview-panel.modal.ask-rovo-links-prompt-heading',
		defaultMessage:
			'Fetch the content behind each of these links and use it as context for subsequent tasks:',
		description:
			'First line of the prompt the Teamwork Graph modal prefills into the Rovo chat input, followed by a numbered list of the urls this content is connected to. The user reads it, can edit it, and presses Enter to send it, so it is translated like any other visible copy.',
	},
});

/**
 * The link-carrying slices of `ConnectionSectionsData`, which satisfies this structurally.
 * Narrowed so a caller can build the prompt from the link bridges alone — see
 * `useShareContextLinksPrompt`.
 */
export type LinksContextConnections = {
	appearsIn: { links: readonly { url: string }[] };
	linksTo: { links: readonly { url: string }[] };
	relatedContent: { links: readonly { url: string }[] };
};

/**
 * The prompt the modal prefills Rovo chat with: every connection url this content has,
 * numbered from 1 in the order the sections are stacked, under a translated heading.
 *
 * Null when there is no url to send, so the button can hide itself.
 */
export const useLinksContextPrompt = (connections: LinksContextConnections): string | null => {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		const links = [
			...connections.appearsIn.links,
			...connections.linksTo.links,
			...connections.relatedContent.links,
		];

		const urls = new Set<string>();

		for (const { url } of links) {
			if (url) {
				urls.add(url);
			}
		}

		return urls.size
			? [
					formatMessage(i18n.promptHeading),
					...[...urls].map((url, index) => `${index + 1}. ${url}`),
				].join('\n')
			: null;
	}, [connections, formatMessage]);
};
